import { memo, useEffect, useMemo } from "react";
import { View } from "react-native";
import { GestureDetector } from "react-native-gesture-handler";
import Animated, {
	Easing,
	useAnimatedStyle,
	useReducedMotion,
	useSharedValue,
	withDelay,
	withTiming,
} from "react-native-reanimated";
import disk_noise_25 from "src/assets/images/disk_noise_25.png";
import isTestUser from "src/constants/isTestUser";
import isWeb from "src/constants/isWeb";
import { MIN_TO_MS, MIN_TO_S } from "src/helpers/constants";
import useDevice from "src/hooks/useDevice";
import usePalette from "src/hooks/usePalette";
import useSpinGesture from "src/hooks/useSpinGesture";
import IconLogo from "src/icons/IconLogo";
import DiscStyles from "src/styles/DiscStyles";
import { Image } from "src/swsh-native";
import ImpactBackgroundGradient from "../ImpactBackgroundGradient";
import WheelActiveIndexManagement from "./components/WheelActiveIndexManagement";
import WheelItem from "./components/WheelItem";
import { WheelProps } from "./types";

const widthRatio = 0.11;
const heightRatio = widthRatio / (5 / 6);
export const DefaultItemDimRatio = { width: widthRatio, height: heightRatio };

const InnerRatio = 0.5;
const RingRatio = 0.01;
const ScrollEnablerRatio = 0.3;
const Wheel = <TItem extends any = unknown>({
	children,
	size,
	numSegments = 14,
	data = [],
	renderItem,
	hasLogo = true,
	itemDimRatio = DefaultItemDimRatio,
	containerStyle,
	onActiveIndexChangeWorklet,
	autoRotateConfig = {
		rotateDuration: 100,
	},
}: WheelProps<TItem> & {
	data?: TItem[];
}) => {
	const innerSize = InnerRatio * size;
	const gestureDetectorDim = useSharedValue({ width: size, height: size });
	const verticalScrollEnablerDim = useSharedValue({
		width: ScrollEnablerRatio * size,
		height: size,
	});
	const { device } = useDevice();
	const { theta, spinGesture, isMoving, scrollEnablerGesture } = useSpinGesture({
		gestureDetectorDim,
		verticalScrollEnablerDim: isWeb ? undefined : verticalScrollEnablerDim,
	});
	useEffect(() => {
		gestureDetectorDim.value = { width: size, height: size };
		verticalScrollEnablerDim.value = {
			width: ScrollEnablerRatio,
			height: size,
		};
	}, [gestureDetectorDim, size, verticalScrollEnablerDim]);

	const reduceMotion = useReducedMotion();
	useEffect(() => {
		if (isMoving) return;
		if (reduceMotion) return;
		if (isTestUser) return;
		if (!autoRotateConfig) return;
		const { rotateDuration = 100 } = autoRotateConfig;
		if (rotateDuration <= 0) return;

		const durationMin = 10;
		// Developer's note: I think it's a cute easter egg that this dies after a certain period of time and needs to be restarted
		theta.value = withDelay(
			0,
			withTiming(theta.value + (2 * Math.PI * durationMin * MIN_TO_S) / rotateDuration, {
				duration: durationMin * MIN_TO_MS,
				easing: Easing.bezier(0.05, 0, 0.25, 1),
			}),
		);
	}, [isMoving, reduceMotion, autoRotateConfig, theta]);

	const rotationOffset = useMemo(() => {
		return (
			Math.PI / 2 +
			// (2 * Math.PI / numSegments) / 2
			// One full circle divided into its segments
			// Then halved for the amount of a half segment
			// Used to ensure Math.PI / 2 is centered (for haptics)
			Math.PI / numSegments
		);
	}, [numSegments]);

	const animatedStyle = useAnimatedStyle(
		() => ({
			transform: [
				{
					rotate: `${theta.value + rotationOffset}rad`,
				},
			],
		}),
		[theta, rotationOffset],
	);

	const parsedData = useMemo(() => {
		const parsedData: (
			| {
					type: "Item";
					item: TItem | null;
			  }
			| {
					type: "Logo";
			  }
		)[] = [];
		let dataIndex = 0;
		for (let i = 0; i < numSegments; i++) {
			if (
				hasLogo &&
				(i === 0 ||
					// Intentionally not rounding
					// If there's an odd number of segments idk which one should be the center so we'll just have one logo
					i === numSegments / 2)
			) {
				parsedData.push({
					type: "Logo",
				});
			} else {
				parsedData.push({
					type: "Item",
					item: data[dataIndex++] ?? null,
				});
			}
		}
		return parsedData;
	}, [data, hasLogo, numSegments]);
	const itemWidth = Math.round(itemDimRatio.width * size);
	const itemHeight = Math.round(itemDimRatio.height * size);

	const centerSize = innerSize - 2 * RingRatio * size;
	const renderedChildren =
		typeof children === "function"
			? children({ width: centerSize, height: centerSize })
			: children;
	const { primary, secondary, isFallback } = usePalette();

	return (
		<>
			{!!onActiveIndexChangeWorklet && (
				<WheelActiveIndexManagement
					theta={theta}
					numSegments={numSegments}
					rotationOffset={rotationOffset}
					onActiveIndexChangeWorklet={onActiveIndexChangeWorklet}
				/>
			)}
			<View
				style={[
					containerStyle,
					{
						alignItems: "center",
						justifyContent: "center",
						// Trying to match the background color of the wheel
						width: size,
						height: size,
						borderRadius: size / 2,
						backgroundColor: secondary,
						shadowColor: isFallback ? DiscStyles.shadowColor : primary,
						shadowRadius: DiscStyles.shadowRadius,
						shadowOpacity: DiscStyles.shadowOpacity,
					},
				]}
			>
				<View
					style={{
						position: "absolute",
						width: size,
						height: size,
						borderRadius: size / 2,
						// Can't apply overflow to parent since it'll remove the shadow
						overflow: "hidden",
					}}
				>
					<Animated.View
						style={[
							animatedStyle,
							{
								position: "absolute",
								width: "100%",
								height: "100%",
								alignItems: "center",
								justifyContent: "center",
								overflow: "hidden",
							},
						]}
					>
						<Image
							alt=""
							pointerEvents="none"
							src={disk_noise_25}
							dim={{
								width: size,
								height: size,
							}}
							style={{
								position: "absolute",
								width: size,
								height: size,
								borderRadius: size / 2,
								overflow: "hidden",
								backgroundColor: secondary,
							}}
						/>
						<ImpactBackgroundGradient
							style={{
								width: innerSize,
								height: innerSize,
								borderRadius: size / 2,
								alignItems: "center",
								justifyContent: "center",
								shadowColor: "black",
								shadowRadius: DiscStyles.shadowRadius,
								shadowOpacity: DiscStyles.shadowOpacity / 2,
								shadowOffset: {
									height: 4,
									width: -4,
								},
							}}
						/>
						{parsedData.map((item, index) => (
							<View
								key={`${index}`}
								style={{
									position: "absolute",
									width: itemWidth,
									height: itemHeight,
									alignItems: "center",
									justifyContent: "center",

									transform: [
										{
											rotate: `${(index * 2 * Math.PI) / numSegments}rad`,
										},
										{
											translateY:
												size / 2 -
												// Divide by 2 for radius, then divide by 2 to get center of the band
												(size - innerSize) / 4,
										},
									],
								}}
							>
								{item.type === "Item" && (
									<WheelItem
										item={item.item}
										width={itemWidth}
										height={itemHeight}
										borderRadius={0.2 * itemWidth}
										index={index}
										renderItem={renderItem}
									/>
								)}
								{item.type === "Logo" && (
									<IconLogo size={0.8 * itemWidth} color="#F0E8D7" />
								)}
							</View>
						))}
					</Animated.View>
				</View>
				<GestureDetector gesture={spinGesture}>
					<View
						style={{
							width: size,
							height: size,
							borderRadius: size / 2,
							overflow: "hidden",
							zIndex: 1,
						}}
					/>
				</GestureDetector>
				{scrollEnablerGesture && (
					<GestureDetector gesture={scrollEnablerGesture}>
						<View
							style={{
								position: "absolute",
								width: ScrollEnablerRatio * size,
								height: size,
								overflow: "hidden",
							}}
						/>
					</GestureDetector>
				)}
				{!scrollEnablerGesture && isWeb && device === "mobile" && (
					<View
						style={{
							position: "absolute",
							width: ScrollEnablerRatio * size,
							height: size,
							overflow: "hidden",
						}}
					/>
				)}
				<View
					style={{
						zIndex: 2,
						position: "absolute",
						width: centerSize,
						height: centerSize,
						flex: 1,
						borderRadius: innerSize / 2,
						overflow: "hidden",
						backgroundColor: secondary,
					}}
				>
					{renderedChildren}
				</View>
			</View>
		</>
	);
};
export default memo(Wheel);
